/**
 * SelectionAuditDetail — the AI Selection Readiness™ report.
 *
 * Renders the V7 structured report in section order: Executive Summary,
 * scorecard (F/T/R + Overall), Strategic Priority, Diagnosis, Selection
 * Journey, the three dimension assessments, Strategic Position, Competitive
 * Selection, Selection Friction, Buyer Selection, the phased Recommendation
 * Roadmap, and the Aurora Insight — plus an expandable Evidence ("show our
 * work") panel and a Markdown export. Polls while the worker drives the run.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type {
  SelectionAudit,
  SelectionAuditEvidence,
  SelectionAuditReport,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { SelectionScorecard } from '../components/SelectionScorecard';
import { auditPillClass } from './SelectionAudits';

const RUNNING_STATUSES = new Set(['pending', 'gathering', 'analyzing']);

const STATUS_MESSAGE: Record<string, string> = {
  pending: 'Queued…',
  gathering: 'Gathering public evidence about the company and competitors…',
  analyzing: 'Running the V7 Selection Readiness auditor…',
};

function Section({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section className="audit-section">
      <h2>
        {n}. {title}
      </h2>
      {children}
    </section>
  );
}

/** A labelled prose block (e.g. "Overall Assessment: …"). */
function Field({
  label,
  value,
}: {
  label: string;
  value: string;
}): JSX.Element | null {
  if (!value || !value.trim()) return null;
  return (
    <p>
      <strong>{label}:</strong> {value}
    </p>
  );
}

function severityPill(s: string): JSX.Element {
  const cls =
    s === 'High'
      ? 'pill pill-failed'
      : s === 'Low'
        ? 'pill pill-complete'
        : 'pill pill-pending';
  return <span className={cls}>{s}</span>;
}

function Report({
  audit,
  report,
}: {
  audit: SelectionAudit;
  report: SelectionAuditReport;
}): JSX.Element {
  return (
    <div className="audit-report">
      <Section n={1} title="Executive Summary">
        {report.executiveSummary.narrative && (
          <p>{report.executiveSummary.narrative}</p>
        )}
        <Field
          label="Overall Assessment"
          value={report.executiveSummary.overallAssessment}
        />
        <Field
          label="Primary Selection Advantage"
          value={report.executiveSummary.primarySelectionAdvantage}
        />
        <Field
          label="Primary Selection Barrier"
          value={report.executiveSummary.primarySelectionBarrier}
        />
        <Field
          label="Strategic Opportunity"
          value={report.executiveSummary.strategicOpportunity}
        />
      </Section>

      <Section n={2} title="AI Selection Readiness™ Scorecard">
        <SelectionScorecard scorecard={report.scorecard} />
        <p className="muted small">
          Scores are directional (0–100), grounded in the gathered evidence —
          not deterministic measurements.
        </p>
      </Section>

      <Section n={3} title="Strategic Priority">
        <p>{report.strategicPriority}</p>
      </Section>

      <Section n={4} title="Selection Readiness Diagnosis">
        <Field
          label="What is helping selection"
          value={report.diagnosis.helping}
        />
        <Field
          label="What is limiting selection"
          value={report.diagnosis.limiting}
        />
        <Field label="What must change" value={report.diagnosis.mustChange} />
      </Section>

      <Section n={5} title="Selection Journey Analysis">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Stage</th>
              <th>Assessment</th>
              <th>Selection Risk</th>
            </tr>
          </thead>
          <tbody>
            {report.selectionJourney.map((row, i) => (
              <tr key={i}>
                <td>{row.stage}</td>
                <td>{row.assessment}</td>
                <td>{row.selectionRisk}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {report.selectionJourneyInterpretation && (
          <Field
            label="Interpretation"
            value={report.selectionJourneyInterpretation}
          />
        )}
      </Section>

      <Section n={6} title="Familiarity Assessment">
        <Field
          label="Strengths"
          value={report.familiarityAssessment.strengths}
        />
        <Field
          label="Weaknesses"
          value={report.familiarityAssessment.weaknesses}
        />
      </Section>
      <Section n={7} title="Trust Assessment">
        <Field label="Strengths" value={report.trustAssessment.strengths} />
        <Field label="Weaknesses" value={report.trustAssessment.weaknesses} />
      </Section>
      <Section n={8} title="Relevance Assessment">
        <Field label="Strengths" value={report.relevanceAssessment.strengths} />
        <Field
          label="Weaknesses"
          value={report.relevanceAssessment.weaknesses}
        />
      </Section>

      <Section n={9} title="Strategic Position Recommendation">
        <Field
          label="Recommended Position"
          value={report.strategicPosition.recommendedPosition}
        />
        <Field
          label="Why It Is Defensible"
          value={report.strategicPosition.whyDefensible}
        />
        <Field
          label="Why Buyers Would Care"
          value={report.strategicPosition.whyBuyersCare}
        />
        <Field
          label="Competitive Advantage Created"
          value={report.strategicPosition.competitiveAdvantage}
        />
      </Section>

      <Section n={10} title="Competitive Selection Analysis">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Competitor</th>
              <th>Why They Win</th>
              <th>Why {audit.companyName} Wins</th>
              <th>Risk Level</th>
            </tr>
          </thead>
          <tbody>
            {report.competitiveSelection.map((row, i) => (
              <tr key={i}>
                <td className="lead-name">{row.competitor}</td>
                <td>{row.whyTheyWin}</td>
                <td>{row.whyThisCompanyWins}</td>
                <td>{row.riskLevel}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {report.competitiveSelectionSynthesis && (
          <Field
            label="Synthesis"
            value={report.competitiveSelectionSynthesis}
          />
        )}
      </Section>

      <Section n={11} title="Selection Friction Analysis">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Issue</th>
              <th>Impact on Selection</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            {report.selectionFriction.map((row, i) => (
              <tr key={i}>
                <td>{row.issue}</td>
                <td>{row.impact}</td>
                <td>{severityPill(row.severity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section n={12} title="Buyer Selection Analysis">
        <table className="leads-table">
          <thead>
            <tr>
              <th>Buyer Type</th>
              <th>Selection Probability</th>
              <th>Opportunity</th>
              <th>Barrier</th>
            </tr>
          </thead>
          <tbody>
            {report.buyerSelection.map((row, i) => (
              <tr key={i}>
                <td className="lead-name">{row.buyerType}</td>
                <td>{row.selectionProbability}</td>
                <td>{row.opportunity}</td>
                <td>{row.barrier}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section n={13} title="Prioritized Recommendation Roadmap">
        <RoadmapPhase
          title="Phase 1: Quick Wins — 60–90 Days"
          items={report.recommendationRoadmap.phase1}
        />
        <RoadmapPhase
          title="Phase 2: Strategic Enhancements — 3–6 Months"
          items={report.recommendationRoadmap.phase2}
        />
        <RoadmapPhase
          title="Phase 3: Strategic Positioning — 6–18 Months"
          items={report.recommendationRoadmap.phase3}
        />
      </Section>

      <Section n={14} title="Aurora Insight">
        <p>{report.auroraInsight}</p>
      </Section>
    </div>
  );
}

function RoadmapPhase({
  title,
  items,
}: {
  title: string;
  items: SelectionAuditReport['recommendationRoadmap']['phase1'];
}): JSX.Element | null {
  if (!items || items.length === 0) return null;
  return (
    <div className="roadmap-phase">
      <h3>{title}</h3>
      <table className="leads-table">
        <thead>
          <tr>
            <th>Recommendation</th>
            <th>Dimension</th>
            <th>Why It Matters</th>
            <th>Business Impact</th>
            <th>Selection Impact</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>{it.recommendation}</td>
              <td>{it.dimension}</td>
              <td>{it.whyItMatters}</td>
              <td>{it.businessImpact}</td>
              <td>{it.selectionImpact}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EvidencePanel({ auditId }: { auditId: string }): JSX.Element {
  const [items, setItems] = useState<SelectionAuditEvidence[] | null>(null);
  const [open, setOpen] = useState(false);

  const load = async (): Promise<void> => {
    try {
      const res = await api.getSelectionAuditEvidence(auditId);
      setItems(res.items);
    } catch {
      setItems([]);
    }
  };

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    if (next && items === null) void load();
  };

  return (
    <section className="audit-section evidence-panel">
      <button type="button" className="btn btn-ghost" onClick={toggle}>
        {open ? 'Hide' : 'Show'} evidence (show our work)
      </button>
      {open && (
        <div>
          {items === null ? (
            <p className="muted">Loading evidence…</p>
          ) : items.length === 0 ? (
            <p className="muted">
              No external evidence was recorded — this audit ran on the model’s
              own knowledge (reduced attribution).
            </p>
          ) : (
            <table className="leads-table">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Dimension</th>
                  <th>Authority</th>
                  <th>Source</th>
                  <th>Snippet</th>
                </tr>
              </thead>
              <tbody>
                {items.map((ev) => (
                  <tr key={ev.id}>
                    <td>{ev.subject}</td>
                    <td>{ev.dimension}</td>
                    <td>{ev.authority}</td>
                    <td>
                      {ev.sourceUrl ? (
                        <a href={ev.sourceUrl} target="_blank" rel="noreferrer">
                          {ev.sourceTitle ?? ev.sourceType ?? ev.sourceUrl}
                        </a>
                      ) : (
                        (ev.sourceTitle ?? ev.sourceType ?? '—')
                      )}
                    </td>
                    <td className="small">{ev.snippet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </section>
  );
}

export function SelectionAuditDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [run, setRun] = useState<SelectionAudit | null>(null);
  const [report, setReport] = useState<SelectionAuditReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const detail = await api.getSelectionAudit(id);
      setRun(detail.run);
      setReport(detail.report);
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Failed to load audit',
      );
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (!run || !RUNNING_STATUSES.has(run.status)) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [run, load]);

  const onExport = async (): Promise<void> => {
    if (!id || !run) return;
    setExporting(true);
    try {
      const res = await api.downloadSelectionAuditMarkdown(id);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${run.companyName.replace(/\s+/g, '-').toLowerCase()}-selection-readiness.md`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  if (error) return <div className="form-error">{error}</div>;
  if (!run) return <div className="muted centered">Loading audit…</div>;

  const running = RUNNING_STATUSES.has(run.status);

  return (
    <div className="audit-detail">
      <div className="page-head">
        <div>
          <Link to="/selection-audits" className="back-link">
            ← All audits
          </Link>
          <h1>{run.companyName} — AI Selection Readiness™</h1>
          <div className="audit-meta muted">
            <span className={auditPillClass(run.status)}>{run.status}</span>
            {run.audience.length > 0 && (
              <span> · Audience: {run.audience.join(', ')}</span>
            )}
            {run.competitorSet.length > 0 && (
              <span> · Competitors: {run.competitorSet.join(', ')}</span>
            )}
            {run.benchmarkRunId && <span> · Benchmark-linked</span>}
            {run.usage && <span> · ${run.usage.cost.toFixed(2)}</span>}
          </div>
        </div>
        {report && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={onExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting…' : 'Download (.md)'}
          </button>
        )}
      </div>

      {run.status === 'failed' && (
        <div className="form-error">
          Audit failed: {run.error ?? 'unknown error'}
        </div>
      )}

      {run.warnings.length > 0 && (
        <details className="audit-warnings">
          <summary className="muted">
            {run.warnings.length} note(s) from this run
          </summary>
          <ul>
            {run.warnings.map((w, i) => (
              <li key={i} className="small muted">
                {w}
              </li>
            ))}
          </ul>
        </details>
      )}

      {running && (
        <div className="muted centered">
          {STATUS_MESSAGE[run.status] ?? 'Working…'} (auto-refreshing)
        </div>
      )}

      {report ? (
        <>
          <Report audit={run} report={report} />
          <EvidencePanel auditId={run.id} />
        </>
      ) : (
        !running && (
          <div className="empty-state muted">No report available.</div>
        )
      )}

      <p className="footer muted small">
        Aurora AI Selection Readiness™ · public evidence only · scores are
        directional · advisory — Aurora never contacts anyone autonomously.
      </p>
    </div>
  );
}
